import queryString from 'query-string'
import { ICaseState } from './Case'

export type SubmittedCaseStatusKey =
	| 'processing'
	| 'not_accepted'
	| 'rejected'
	| 'pipedrive_won'
	| 'pipedrive_open'
	| 'client_approval'
	| 'on_hold'
	| 'closed'

// Who rejected the case behind a submission. `partner` is the rights holder's
// own decision in client review, `operations` is one of ours in the back
// office, `system` is an automatic rejection with no human behind it.
export type RejectionSource = 'partner' | 'operations' | 'system'

export interface ISubmittedCaseRejection {
	source: RejectionSource
	reason: string | null
	occurred_at: string | null
}

export interface ISubmittedCase {
	url?: string
	state?: ICaseState | null
	violation_id?: string | null
	case_id?: string | null
	rejected_reason?: string | null
	// True when the submission did not create the case above but landed on one we
	// already held for the same URL and image.
	matched_existing_violation?: boolean
	// Parked by the partner in client review. Not a state: a case keeps its real
	// state (normally client_approval) while on hold, so this rides alongside it.
	on_hold?: boolean
	rejection?: ISubmittedCaseRejection | null
	// The API's verdict on whether resubmitting would actually reopen this case.
	// The resubmit button hangs on this alone — a case the pipeline would drop
	// again must not be offered an action that silently does nothing.
	can_resubmit?: boolean
	submitted_case_id?: string
}

// Partner-facing labels for the violation states we expose. Internal pipeline
// states (stage_01/02/03, contact_enrichment, screenshot_review,
// ready_for_export) are intentionally omitted -> they read as "Processing".
export const PARTNER_STATE_LABEL: Partial<Record<ICaseState, string>> = {
	pipedrive_won: 'caseState/pipedrive_won', // Won
	pipedrive_open: 'caseState/open', // Open
	client_approval: 'caseState/client_approval', // Waiting for review
	on_hold: 'caseState/on_hold', // On Hold
	pipedrive_lost: 'caseState/closed', // Closed
	rejected: 'createCase/rejected', // Rejected
	// No `on_hold` entry, and none should be added: on hold is not a state. It is
	// a client_review.cases_on_hold row that leaves the case in its real state,
	// so it arrives as ISubmittedCase.on_hold and is handled ahead of this map.
}

// Canonical status used for filtering. Mirrors the CreatedCaseStatus display
// logic, grouping only pipedrive_lost under "closed" — `rejected` is its own
// status, because a partner needs to tell "we stopped pursuing this" apart from
// "the case ran its course".
export function getSubmittedCaseStatusKey(
	theCase: ISubmittedCase,
): SubmittedCaseStatusKey {
	if (theCase.rejected_reason) return 'not_accepted'
	if (theCase.violation_id && theCase.state) {
		if (theCase.state === 'rejected') return 'rejected'
		if (theCase.state === 'pipedrive_lost') return 'closed'
		// Ahead of the state label, and not gated on client_approval: putOnHold
		// does not restrict which state a case can be parked in, so gating would
		// silently hide one parked anywhere else.
		if (theCase.on_hold) return 'on_hold'
		if (PARTNER_STATE_LABEL[theCase.state])
			return theCase.state as SubmittedCaseStatusKey
	}
	return 'processing'
}

// The reasons a rejection can carry, mapped to partner-facing text.
//
// The first group is what a human picks in review (client_case_review_events
// .event_type); the second is written by the pipeline itself. A reason missing
// from here is never shown raw — getRejectionDetail falls back to the generic
// wording rather than leaking an internal slug at a partner.
//
// That fallback is a dead end for the partner, so this map has to cover every
// reason a rejection can actually carry. Three vocabularies write into it and
// none of them share spelling, so adding a reason on either side without adding
// it here silently turns those cases into "Contact us to find out why":
//
//   1. api server/violations/RejectReason.ts — RejectReasonEnum, the camelCase
//      slugs the frontoffice review UI writes (its own copy of the list is
//      frontoffice src/lib/reviewReasons.ts, which calls them a contract).
//   2. The PascalCase reasons the client-review and case-preparation paths
//      write.
//   3. The pipeline's own strings, which are neither.
export const REJECTION_REASON_LABEL: Record<string, string> = {
	NotViolating: 'createCase/rejectionReason/NotViolating',
	Whitelisted: 'createCase/rejectionReason/Whitelisted',
	Handout: 'createCase/rejectionReason/Handout',
	SingleSale: 'createCase/rejectionReason/SingleSale',
	OutOfCopyright: 'createCase/rejectionReason/OutOfCopyright',
	UnknownPhotographer: 'createCase/rejectionReason/UnknownPhotographer',
	UnknownDate: 'createCase/rejectionReason/UnknownDate',
	CounterpartyOnPicture: 'createCase/rejectionReason/CounterpartyOnPicture',
	NoContactInformation: 'createCase/rejectionReason/NoContactInformation',
	OtherReason: 'createCase/rejectionReason/OtherReason',
	OtherUnclearReason: 'createCase/rejectionReason/OtherReason',
	'image-not-present': 'createCase/rejectionReason/imageNotPresent',
	OutOfMarket: 'createCase/rejectionReason/outOfMarket',

	// RejectReasonEnum, written by POST /violations/set-action/:violation_id.
	// 'other' is the same decision as OtherReason above and shares its wording.
	other: 'createCase/rejectionReason/OtherReason',
	byline: 'createCase/rejectionReason/byline',
	blacklistDomain: 'createCase/rejectionReason/blacklistDomain',
	blacklistImage: 'createCase/rejectionReason/blacklistImage',
	multicase: 'createCase/rejectionReason/multicase',
	redirect: 'createCase/rejectionReason/redirect',
	teaser: 'createCase/rejectionReason/teaser',
	insufficientContactInfo: 'createCase/rejectionReason/insufficientContactInfo',
	// Not an abandonment: POST /violations/:violation_id/inaccurate-url rejects
	// the violation and republishes the lead on the corrected URL, so the case
	// carries on as a new one. The wording has to say so.
	inaccurateUrl: 'createCase/rejectionReason/inaccurateUrl',

	// Written by the pipeline, not by a reviewer.
	'Closed by script': 'createCase/rejectionReason/closedByScript',
	MissingDestinationImageUrl:
		'createCase/rejectionReason/missingDestinationImage',
}

export interface RejectionDetail {
	// i18n key for the sentence shown under the "Rejected" label.
	labelKey: string
	// i18n key for the reason clause interpolated into it, if we have one.
	reasonKey: string | null
}

/**
 * The explanation shown under a rejected submission.
 *
 * A partner who submits a case and is told only "Rejected" has no way to tell a
 * decision they made themselves from one of ours, so every branch here resolves
 * to a sentence that names both who decided and why. Where we cannot name the
 * reason, it says so and points at their account manager rather than inventing
 * one or showing an internal slug.
 */
export function getRejectionDetail(theCase: ISubmittedCase): RejectionDetail {
	const rejection = theCase.rejection
	const reasonKey = rejection?.reason
		? REJECTION_REASON_LABEL[rejection.reason] ?? null
		: null

	if (rejection?.source === 'partner') {
		// Resubmitting a case the partner decided on does not reopen it — the
		// previous-violation filter drops it — so the "earlier" wording says so.
		// Each of the two comes in a with-reason and a without-reason form: a
		// partner picks their own reason in review, but not every reason has
		// partner-facing wording, and a sentence built around "because {{reason}}"
		// reads as "because ." when the interpolation is empty.
		const earlier = theCase.matched_existing_violation
		return {
			labelKey: reasonKey
				? earlier
					? 'createCase/rejectionDetail/dismissedByYouEarlier'
					: 'createCase/rejectionDetail/dismissedByYouBecause'
				: earlier
				? 'createCase/rejectionDetail/dismissedByYouEarlierNoReason'
				: 'createCase/rejectionDetail/dismissedByYou',
			reasonKey,
		}
	}

	if (!reasonKey) {
		// Still say whether this submission landed on a case we already held. Not
		// being able to name the reason is no reason to also drop that: without it
		// a duplicate reads as though this submission was the one we judged.
		return {
			labelKey: theCase.matched_existing_violation
				? 'createCase/rejectionDetail/unknownEarlier'
				: 'createCase/rejectionDetail/unknown',
			reasonKey: null,
		}
	}

	if (rejection?.source === 'operations') {
		return {
			labelKey: theCase.matched_existing_violation
				? 'createCase/rejectionDetail/rejectedByUsEarlier'
				: 'createCase/rejectionDetail/rejectedByUs',
			reasonKey,
		}
	}

	return {
		labelKey: theCase.matched_existing_violation
			? 'createCase/rejectionDetail/rejectedAutomaticallyEarlier'
			: 'createCase/rejectionDetail/rejectedAutomatically',
		reasonKey,
	}
}

// Options for the status filter (id -> i18n label key).
export const STATUS_FILTER_OPTIONS: Array<{
	id: SubmittedCaseStatusKey
	labelKey: string
}> = [
	{ id: 'processing', labelKey: 'createCase/processing' },
	{ id: 'not_accepted', labelKey: 'createCase/notAccepted' },
	{ id: 'client_approval', labelKey: 'caseState/client_approval' },
	{ id: 'pipedrive_open', labelKey: 'caseState/open' },
	{ id: 'on_hold', labelKey: 'caseState/on_hold' },
	{ id: 'pipedrive_won', labelKey: 'caseState/pipedrive_won' },
	{ id: 'rejected', labelKey: 'createCase/rejected' },
	{ id: 'closed', labelKey: 'caseState/closed' },
]

export const statusLabelKey = (id: string): string | undefined =>
	STATUS_FILTER_OPTIONS.find((o) => o.id === id)?.labelKey

// Filters used by the submitted-cases SearchBar. `searchTerm` is the URL search
// field; `status` is the funnel filter (multi-select of status keys).
export interface SubmittedCaseFilters {
	searchTerm: string
	status: string[]
}

export const getDefaultSubmittedFilters = (): SubmittedCaseFilters => ({
	searchTerm: '',
	status: [],
})

// Reads the filters the SearchBar wrote into the URL (query-string, comma arrays).
export function parseSubmittedFilters(search: string): SubmittedCaseFilters {
	const parsed = queryString.parse(search, { arrayFormat: 'comma' })
	const term = parsed.searchTerm
	const status = parsed.status
	return {
		searchTerm: typeof term === 'string' ? term : '',
		status: Array.isArray(status)
			? status.filter((s): s is string => Boolean(s))
			: status
			? [status]
			: [],
	}
}
